import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { consentFormsAPI, ConsentForm } from '../services/api';
import { 
  FileText, 
  Search,
  ArrowLeft,
  Loader,
  AlertCircle,
  User,
  Calendar,
  Eye,
  CheckCircle,
  XCircle,
  Camera,
  Download
} from 'lucide-react';

const ConsentFormsList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [consentForms, setConsentForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState<ConsentForm | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState<string | null>(null);

  useEffect(() => {
    loadConsentForms();
  }, []);

  const loadConsentForms = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await consentFormsAPI.getAll();
      setConsentForms(data);
    } catch (err: any) {
      setError('Error al cargar consentimientos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const filteredForms = consentForms.filter(form => {
    const searchLower = searchTerm.toLowerCase();
    const patientName = form.patient_data?.nombre?.toLowerCase() || '';
    const patientDoc = form.patient_data?.documento?.toLowerCase() || '';
    const templateTitle = form.template_title?.toLowerCase() || '';
    
    return patientName.includes(searchLower) || 
           patientDoc.includes(searchLower) || 
           templateTitle.includes(searchLower);
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const viewDetails = (form: ConsentForm) => {
    setSelectedForm(form);
    setShowDetailsModal(true);
  };

  const downloadPDF = async (formId: string, patientName: string, patientDoc: string) => {
    setDownloadingPDF(formId);
    try {
      const blob = await consentFormsAPI.downloadPDF(formId);
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nombre del archivo
      const safeName = patientName.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
      link.download = `Consentimiento_${safeName}_${patientDoc}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error descargando PDF:', err);
      setError('Error al descargar el PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloadingPDF(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando consentimientos…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-7xl mx-auto">
        <div className="page-hero mb-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate('/templates')}
              className="btn-secondary mb-4 py-2 text-sm"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a plantillas
            </button>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
              Registro
            </p>
            <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Consentimientos guardados
            </h1>
            <p className="max-w-2xl text-sm text-slate-600 sm:text-base">
              Consulte, audite visualmente y descargue PDF de los consentimientos registrados.
            </p>
          </div>
          <div className="flex shrink-0 items-center rounded-xl border border-slate-200/90 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm">
            Total: {consentForms.length} consentimiento{consentForms.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="card mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre del paciente, documento o tipo de consentimiento..."
              className="input-field pl-12"
            />
          </div>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de consentimientos */}
        {filteredForms.length === 0 ? (
          <div className="card text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm ? 'No se encontraron consentimientos' : 'No hay consentimientos guardados'}
            </h3>
            <p className="text-gray-500">
              {searchTerm 
                ? 'Intenta con otro término de búsqueda'
                : 'Los consentimientos guardados aparecerán aquí'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredForms.map((form) => (
              <div
                key={form.id}
                className="card hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                      {form.template_title || 'Consentimiento'}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <User className="w-4 h-4" />
                        <span className="font-semibold">{form.patient_data?.nombre || 'N/A'}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">Documento:</span> {form.patient_data?.documento || 'N/A'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(form.filled_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {form.consent_responses?.accepts_consent === true ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : form.consent_responses?.accepts_consent === false ? (
                      <XCircle className="w-6 h-6 text-red-600" />
                    ) : null}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                  {form.patient_photo && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Camera className="w-4 h-4" />
                      <span>Con foto</span>
                    </div>
                  )}
                  <div className="flex-1"></div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (!form.id) return;
                        const patientName: string = form.patient_data?.nombre ?? 'paciente';
                        const patientDoc: string = form.patient_data?.documento ?? 'sin_doc';
                        downloadPDF(form.id, patientName, patientDoc);
                      }}
                      disabled={downloadingPDF === form.id || !form.id}
                      className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Descargar PDF"
                    >
                      {downloadingPDF === form.id ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          PDF
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => viewDetails(form)}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Detalles */}
        {showDetailsModal && selectedForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Detalles del Consentimiento</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedForm(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Información del Consentimiento */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Información del Consentimiento</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Tipo de Consentimiento</label>
                      <p className="text-gray-700">{selectedForm.template_title || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Fecha de Llenado</label>
                      <p className="text-gray-700">{formatDate(selectedForm.filled_at)}</p>
                    </div>
                    <div>
                      <label className="label">Llenado por</label>
                      <p className="text-gray-700">{selectedForm.filled_by || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Estado</label>
                      <div className="flex items-center gap-2">
                        {selectedForm.consent_responses?.accepts_consent === true ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-600 font-semibold">Aceptado</span>
                          </>
                        ) : selectedForm.consent_responses?.accepts_consent === false ? (
                          <>
                            <XCircle className="w-5 h-5 text-red-600" />
                            <span className="text-red-600 font-semibold">Rechazado</span>
                          </>
                        ) : (
                          <span className="text-gray-500">Pendiente</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="label">Autorización Digital</label>
                      <p className="text-gray-700">
                        {selectedForm.consent_responses?.accepts_digital ? 'Sí' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información del Paciente */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Información del Paciente</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Nombre Completo</label>
                      <p className="text-gray-700">{selectedForm.patient_data?.nombre || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Documento</label>
                      <p className="text-gray-700">{selectedForm.patient_data?.documento || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Sexo</label>
                      <p className="text-gray-700">{selectedForm.patient_data?.sexo || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Edad</label>
                      <p className="text-gray-700">
                        {selectedForm.patient_data?.edad ? `${selectedForm.patient_data.edad} años` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="label">Fecha de Nacimiento</label>
                      <p className="text-gray-700">{selectedForm.patient_data?.fecha_nacimiento || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="label">Teléfono</label>
                      <p className="text-gray-700">{selectedForm.patient_data?.telefono || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Foto del Paciente */}
                {selectedForm.patient_photo && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Foto del Paciente</h3>
                    <div className="flex justify-center">
                      <img 
                        src={selectedForm.patient_photo} 
                        alt="Foto del paciente" 
                        className="max-w-md rounded-lg border-2 border-gray-300"
                      />
                    </div>
                  </div>
                )}

                {/* Huella digital del Paciente (opcional) */}
                {selectedForm.patient_fingerprint && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Huella digital del Paciente</h3>
                    <div className="flex justify-center">
                      <img 
                        src={selectedForm.patient_fingerprint} 
                        alt="Huella del paciente" 
                        className="max-w-[200px] rounded-lg border-2 border-gray-300"
                      />
                    </div>
                  </div>
                )}

                {/* Firmas */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Firmas</h3>
                  <div className="space-y-4">
                    {/* Firma del Paciente */}
                    {selectedForm.signatures?.patient_signature && (
                      <div>
                        <label className="label">Firma del Paciente</label>
                        <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                          <img 
                            src={selectedForm.signatures.patient_signature} 
                            alt="Firma del paciente" 
                            className="max-w-full max-h-48 mx-auto"
                          />
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <p><strong>Nombre:</strong> {selectedForm.signatures.patient_name || 'N/A'}</p>
                          <p><strong>Documento:</strong> {selectedForm.signatures.patient_document || 'N/A'}</p>
                        </div>
                      </div>
                    )}

                    {/* Firma del Responsable */}
                    {selectedForm.signatures?.responsible_signature && (
                      <div>
                        <label className="label">Firma del Responsable/Acudiente</label>
                        <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                          <img 
                            src={selectedForm.signatures.responsible_signature} 
                            alt="Firma del responsable" 
                            className="max-w-full max-h-48 mx-auto"
                          />
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <p><strong>Nombre:</strong> {selectedForm.signatures.responsible_name || 'N/A'}</p>
                          <p><strong>Documento:</strong> {selectedForm.signatures.responsible_document || 'N/A'}</p>
                        </div>
                      </div>
                    )}

                    {!selectedForm.signatures?.patient_signature && !selectedForm.signatures?.responsible_signature && (
                      <p className="text-gray-500 text-center py-4">No hay firmas registradas</p>
                    )}
                  </div>
                </div>

                {/* Información del Profesional */}
                {selectedForm.consent_responses?.nombre_profesional && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Información del Profesional</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Nombre del Profesional</label>
                        <p className="text-gray-700">{selectedForm.consent_responses.nombre_profesional || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="label">Rol/Especialidad</label>
                        <p className="text-gray-700">{selectedForm.consent_responses.rol_profesional || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-between">
                <button
                  onClick={() => {
                    if (!selectedForm || !selectedForm.id) return;
                    const patientName: string = selectedForm.patient_data?.nombre ?? 'paciente';
                    const patientDoc: string = selectedForm.patient_data?.documento ?? 'sin_doc';
                    downloadPDF(selectedForm.id, patientName, patientDoc);
                  }}
                  disabled={downloadingPDF === selectedForm?.id || !selectedForm?.id}
                  className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingPDF === selectedForm?.id ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generando PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Descargar PDF
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedForm(null);
                  }}
                  className="btn-secondary"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentFormsList;
